/**
 * SrPlaybackBar — Synth VR editor bottom bar.
 *
 * Experimental single-row layout (SR only — the other editors keep the shared
 * EditorPlaybackBar 3-pill layout):
 *   [ transport pill ] [ waveform (fills centre) ] [ song-info pill (right) ]
 * The note-count / beat readout is removed; the song cover + name + artist now
 * lives in the right pill. The waveform (Timeline) is embedded in the centre.
 */

import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { getArtworkUrl } from '../../api/client';
import { transportStyle, transportBtn } from '../ui/editorSharedStyles';
import { color, font } from '../ui/theme';
import { IcTriRight } from '../ui/icons';
import SongTag from '../ui/SongTag';
import Timeline from './Timeline';
import SrHintBar from './SrHintBar';
import AudioBars from './AudioBars';

const fmtTime = (s: number) => {
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

export default function SrPlaybackBar() {
  const beatmap = useEditorStore(s => s.beatmap);
  const playback = useEditorStore(s => s.playback);
  const setPlaying = useEditorStore(s => s.setPlaying);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);
  const setEditInfoOpen = useEditorStore(s => s.setEditInfoOpen);

  if (!beatmap) return null;

  return (
    // 3-column grid (transport · waveform · song). The hint sits in row 1 of the
    // middle column, so it's exactly the waveform's width and position.
    // Grid template lives in App.css (.sr-playbar) so media queries can
    // reshape it on narrow viewports.
    <div className="sr-playbar">
      {/* Row 1 — hint aligned to the waveform column */}
      <div />
      <SrHintBar />
      <div />

      {/* Row 2, col 1 — Transport. Small right padding so the spectrum pill
          nestles into the container's right curve. */}
      <div className="sr-pb-pill" style={{ ...barPillStyle, paddingLeft: 6, paddingRight: 4 }}>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setCurrentTime(0); }}
          style={transportBtnRound}
          title="Rewind"
          data-no-tint-hover
          className="accent-fill-hover"
          onMouseEnter={roundHoverIn} onMouseLeave={roundHoverOut}
        >⏮</button>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setPlaying(!playback.playing); }}
          style={transportBtnRound}
          title="Play / Pause"
          data-no-tint-hover
          className={playback.playing ? 'accent-fill-hover accent-fill-active' : 'accent-fill-hover'}
          onMouseEnter={roundHoverIn} onMouseLeave={roundHoverOut}
        >{playback.playing ? '❚❚' : <IcTriRight size={14} />}</button>
        {/* Timer centred between the buttons and the visualizer */}
        <span style={{ ...timeReadoutStyle, flex: 1, textAlign: 'center' }}>
          {fmtTime(playback.currentTime)} / {fmtTime(playback.duration)}
        </span>
        <div className="sr-viz-pill" style={vizPillStyle}>
          <AudioBars width={56} height={24} />
        </div>
      </div>

      {/* Row 2, col 2 — Waveform (fills the centre). overflow:hidden clips the
          timeline's fixed right-edge controls on very narrow viewports instead
          of letting them spill past the screen edge. */}
      <div style={{ minWidth: 0, display: 'flex', overflow: 'hidden' }}>
        <Timeline />
      </div>

      {/* Row 2, col 3 — Song info. Small left padding so the round vinyl cover
          nestles into the pill's left curve. */}
      <div className="sr-pb-pill sr-song-pill" style={{ ...barPillStyle, paddingLeft: 6 }}>
        <SongTag
          bare
          spinning={playback.playing}
          coverUrl={beatmap.id ? getArtworkUrl(beatmap.id) : null}
          primary={beatmap.name?.trim() || '(untitled)'}
          secondary={beatmap.author?.trim() || 'Unknown artist'}
          duration={0}
          onClick={() => setEditInfoOpen(true)}
        />
      </div>
    </div>
  );
}

const barPillStyle: React.CSSProperties = {
  ...transportStyle,
  borderRadius: 999,
  gap: 10,
  height: 48,
  padding: '0 16px',
  alignItems: 'center',
};

const transportBtnRound: React.CSSProperties = {
  ...transportBtn,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s ease, color 0.15s ease',
};

// Hover for the round transport buttons: fill the container with the accent and
// flip the icon to the dark tint (matches the stepper arrows).
const roundHoverIn = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'oklch(0.62 calc(0.14 * var(--accent-c)) var(--accent-h))';
  e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))';
};
const roundHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  e.currentTarget.style.color = 'var(--accent)';
};

const timeReadoutStyle: React.CSSProperties = {
  color: color.ink,
  fontFamily: font.sans,
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

// Live-spectrum pill on the right of the playback bar. Sized to match the two
// transport buttons: same height (36, like the play button) and as wide as the
// two buttons together (36 + 10 gap + 36 = 82).
const vizPillStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 82,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};
